import React, { memo, useCallback, useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import interactionResponse from 'await-interaction-response';
import clsx from 'clsx';
import _isEqual from 'lodash/isEqual';

import { useFeatures } from '@atc/features';

import { Carousel, Chip } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { compareFilterChipsList } from '@/utilities/filterChipUtil';

import { clearChipClick } from '@/analytics/srp/srpSearchEvents';

import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpSelectedFiltersInOrderDuck from '@/ducks/srp/srpSelectedFiltersInOrderDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

function ChipsWrapperComponent({ children, isWrapped, container, containerProps }) {
    if (isWrapped) {
        return React.createElement(container, containerProps, children);
    }
    return children;
}

export function InventoryFiltersChipsContainer({
    showFallbackMessage = true,
    showFilterChips = true,
    noWrap = false,
    carouselProps = {},
}) {
    const dispatch = useDispatch();

    const {
        carbravo_entry_points: [enableCarBravoEntryPoints],
        clear_experience: [isClearExperienceEnabled],
        filter_chips_srp: [enableFilterChips],
        srp_body_style_filter_override: [enableCommercialBodyStyleFilterOverride, {
            label: commercialBodyStyleLabel,
        }],
    } = useFeatures([
        'carbravo_entry_points',
        'clear_experience',
        'filter_chips_srp',
        'srp_body_style_filter_override',
    ]);

    const navigateToSrp = useSrpNavigation();
    const activeFilterValues = useSelector(srpFiltersDuck.selectors.getSelectedFilterChipsInOrder, _isEqual);

    useEffect(() => {
        // Comparing logic in React.memo prevents the component from re-rendering,
        // therefore this update does not trigger a new re-render of the component
        dispatch(srpSelectedFiltersInOrderDuck.creators.setSelectedFiltersInOrder({ filterChipsInOrder: activeFilterValues }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilterValues]);

    const deleteAction = useCallback((filter) => {
        let payload = '';

        switch (filter.name) {
            case 'maxPrice':
            case 'minPrice': {
                payload = ['minPrice', 'maxPrice'];
                dispatch(srpFiltersDuck.creators.clearFilters(payload));
                break;
            }
            case 'startYear':
            case 'endYear': {
                payload = ['startYear', 'endYear'];
                dispatch(srpFiltersDuck.creators.clearFilters(payload));
                break;
            }
            default:
                payload = {
                    filter,
                    value: filter.value,
                    isMultiple: filter.isMultiple,
                };
                dispatch(srpFiltersDuck.creators.deselectFilter(payload));
        }
    }, [dispatch]);

    const handleClearChip = useCallback(async (event, filter) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;
        sendClick(clearChipClick, event, {
            removedValue: filter.label,
        });

        // update redux state
        deleteAction({
            name: filter.filterName,
            value: filter.value,
            isMultiple: true,
        });

        const shouldClearExperience = isClearExperienceEnabled && ['BUY_ONLINE', 'HYUND'].includes(filter?.value);
        const shouldClearExperienceCarProduct = isClearExperienceEnabled && enableCarBravoEntryPoints && ['car-bravo'].includes(filter?.value);

        let query = {};

        if (shouldClearExperience) {
            query = { experience: null };
        }

        if (shouldClearExperienceCarProduct) {
            query = { experience: null, carProduct: null };
        }

        await navigateToSrp({ filtersValues: query, resetPagination: true, isNewSearch: true });
    }, [deleteAction, navigateToSrp, isClearExperienceEnabled, enableCarBravoEntryPoints]);

    const noSelectionMessage = !showFallbackMessage ? null : (
        <span className="text-subdued-lightest"> Select filters to narrow your results. </span>
    );

    return (enableFilterChips && showFilterChips && activeFilterValues?.length > 0) ? (
        <span
            data-cmp="filterChips"
            className={clsx('padding-vertical-2', { 'display-flex flex-nowrap': noWrap })}
        >
            <ChipsWrapperComponent
                isWrapped={Object.keys(carouselProps).length > 0}
                container={Carousel}
                containerProps={{ ...carouselProps }}
            >
                {activeFilterValues.map((filter) => {
                    const displayFilter = enableCommercialBodyStyleFilterOverride && filter.value === 'VANMV'
                        ? { ...filter, label: commercialBodyStyleLabel }
                        : filter;

                    return (
                        <Chip
                            glyph="remove"
                            uiContext="input"
                            className="margin-bottom-2 margin-bottom-sm-0"
                            key={`filter-chip-${filter.label}-${filter.filterName}`}
                            onClick={(event) => handleClearChip(event, filter)}
                            {...displayFilter}
                        />
                    );
                })}
            </ChipsWrapperComponent>
        </span>
    ) : noSelectionMessage;
}

export default memo(InventoryFiltersChipsContainer, (oldProps, newProps) => {
    const { activeFilterValues: oldActiveFilterValues, ...restOldProps } = oldProps;
    const { activeFilterValues: newActiveFilterValues, ...restNewProps } = newProps;

    return _isEqual(restOldProps, restNewProps) && compareFilterChipsList(oldActiveFilterValues, newActiveFilterValues);
});
